import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Link,
  Divider,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Circle,
  Upload,
  History,
  Download,
  Delete,
  Visibility,
  CloudUpload,
} from '@mui/icons-material';
import { applications } from '../../utils/variables';
import { ProjectFileUploadResponse } from '../../types/projects';
import Foundation_Icon from '../../assets/Foundation_IconOnly_CMYK.png';
import Image from 'mui-image';
import { formClient } from '../../utils/httpClientTs';
import dayjs from 'dayjs';

// Test data for file history
const testFileHistory: ProjectFileUploadResponse[] = [
  {
    id: '1',
    name: 'Foundation_ICH_2024_001.xlsx',
    uploaded_at: '2024-01-15T10:30:00Z',
    uploaded_by: 'Jane Smith',
    status: 'Completed',
    number_of_errors: 0,
  },
  {
    id: '2',
    name: 'Foundation_ICH_2024_002.csv',
    uploaded_at: '2024-01-10T14:22:00Z',
    uploaded_by: 'Mary Johnson',
    status: 'Processing',
    number_of_errors: 2,
  },
  {
    id: '3',
    name: 'Foundation_ICH_2024_003.xls',
    uploaded_at: '2024-01-05T09:15:00Z',
    uploaded_by: 'Sarah Wilson',
    status: 'Completed',
    number_of_errors: 0,
  },
];

// Mock error data for demonstration
const mockErrors: { [key: string]: string[] } = {
  '2': [
    'Missing signature on page 3',
    'Incomplete financial information in section 2',
  ],
};

interface ButtonLinkProps {
  href: string;
  label: string;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({ href, label }) => (
  <Button
    size="small"
    variant="text"
    color="primary"
    href={href}
    target="_blank"
    rel="noopener"
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(80, 158, 47, 0.08)',
      },
      color: '#509E2F',
    }}
  >
    {label}
  </Button>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`foundation-tabpanel-${index}`}
    aria-labelledby={`foundation-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const FoundationLandingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {}
  );

  const canUploadForm = () => {
    const userApplications = localStorage.getItem('apps');
    return (
      userApplications && userApplications.includes(applications.Foundations)
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const sendDocument = async (fileData) => {
    await formClient.post('/projects/foundation/import', fileData);
    //refetch();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('project', 'Foundation');

      // Call the sendDocument function
      await sendDocument(formData);

      setUploading(false);
      setUploadSuccess(true);
      setSelectedFile(null);

      // Close dialog after showing success
      setTimeout(() => {
        setUploadDialogOpen(false);
        setUploadSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploading(false);
      // You could add error state handling here if needed
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Processing':
        return 'warning';
      case 'Error':
        return 'error';
      default:
        return 'default';
    }
  };

  const toggleRowExpansion = (fileId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [fileId]: !prev[fileId],
    }));
  };

  const ichFormLink = () => {
    let now = new Date();
    let newFormDate = new Date(2026, 2, 1);
    if (now >= newFormDate) {
      return 'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A364E72766B497571375A786F752F5348516264757775446750544935425472584D445A5363324E54585769786C4E7573487153386C343D';
    } else {
      return 'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A7752317746557165454E5141617A32515039397456634F535365325359776141444963665755595A6B544F6B306B7A6F574E2B74756B3D';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Hero Section with Foundation Logo */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #509E2F 0%, #0072CE 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Image
            src={Foundation_Icon}
            alt="Foundation Logo"
            sx={{ maxWidth: '100px' }}
            width={'40vw'}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1.5, color: '#fff' }}
        >
          P.E.O. Foundation
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', color: '#fff' }}
        >
          The P.E.O. Foundation provides scholarships and educational assistance
          to women through various funds and programs, supporting their
          educational goals and aspirations.
        </Typography>
      </Paper>

      {/* Tab Navigation */}
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Foundation page tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab label="Information & Resources" />
            {canUploadForm() && <Tab label="File Management" />}
          </Tabs>
        </Box>

        {/* Information & Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Awarding a Chapter Scholarship */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Awarding a Chapter Scholarship"
              sx={{
                background: 'linear-gradient(135deg, #509E2F 0%, #0072CE 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ pb: 4 }}>
              <Grid container xs={12}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    Use the information below if your chapter has established a
                    Cottey College Scholarship Fund or General Scholarship Fund
                    in the P.E.O. Foundation.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    Due to our charitable classification and in order to comply
                    with the P.E.O. Foundation requirements and IRS regulations,
                    selection of recipients must be in accordance with
                    established guidelines provided by the P.E.O. Foundation.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    These procedures involve using objective standards in
                    selecting recipients and maintaining adequate records to
                    show that specific procedures are being followed.
                    Scholarships are for women who are high school graduates or
                    equivalent and are enrolled in an accredited post-secondary
                    institution in the U.S. or Canada. Recipient must be a
                    citizen or legal permanent resident of the U.S. or Canada
                    and must be able to provide proof of such if requested.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    Each applicant must be considered on the basis of her
                    qualifications. Selection committees should establish
                    written criteria by which to evaluate an applicant's
                    qualifications. Such criteria may include, but are not
                    limited to: scholastic standing, financial need, personal
                    recommendations, plans for use of education and acceptance
                    by institution of choice. These criteria must adhere to
                    restrictions specified by the fund, if any (e.g., field of
                    study, residency requirement, institution to be attended,
                    etc.).
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    All funds held in the P.E.O. Foundation require a chair, and
                    their contact information must be kept up to date. If you
                    need to change the chair of the fund, please fill out the
                    Fund Chair Change Form linked below.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" fontWeight="bold" sx={{ p: 2 }}>
                    Tips for Submitting the Individual Case History:
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    Before beginning the Online Individual Case History Form
                    (below), you must have completed the Individual Case History
                    Worksheet that you will reference while completing
                    information on the Online Individual Case History Form.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    Your Individual Case History will be reviewed by the
                    Foundation office to ensure all necessary information has
                    been provided. Your chapter fund chair will receive emails
                    throughout the process.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    The entire process may take 4 weeks, depending on how
                    quickly the trustees are able to review the ICH. Please be
                    patient before sending an email to the Foundation office to
                    request a status update.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" fontWeight="bold" sx={{ p: 2 }}>
                    Deadlines:
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    fontStyle="italic"
                    sx={{ pt: 2, pl: 2 }}
                  >
                    Scholarship for Fall Term
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ pb: 2, pl: 2, lineHeight: 1.6 }}
                  >
                    All required documents must be received by the P.E.O.
                    Foundation office no later than December 1. For example, if
                    the student receives a scholarship for the fall term, then
                    the documents must be received no later than December 1 of
                    that term.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    fontStyle="italic"
                    sx={{ pt: 2, pl: 2 }}
                  >
                    Scholarship for Spring Term
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ pb: 2, pl: 2, lineHeight: 1.6 }}
                  >
                    All required documents must be received by the P.E.O.
                    Foundation office no later than March 1. For example, if the
                    student receives a scholarship for the spring term, then the
                    documents must be received no later than March 1 of that
                    same term.
                  </Typography>
                </Grid>

                <Grid item xs={12} sx={{ p: 2 }}>
                  <Divider />
                </Grid>
                <Grid item xs={6} sx={{ pl: 2, pr: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #509E2F 0%, #0072CE 100%)',
                      color: '#fff',
                      borderRadius: 1,
                    }}
                  >
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ pl: 2, pr: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #509E2F 0%, #0072CE 100%)',
                      color: '#fff',
                      borderRadius: 1,
                    }}
                  >
                    Resource
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ p: 2 }}>
                    Individual Case History Worksheet
                  </Typography>
                </Grid>
                <Grid item container xs={6} spacing={1} sx={{ p: 2 }}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://www.peointernational.org/wp-content/uploads/2024/02/individualcasehistoryworksheetaugust2023.pdf"
                      label="Worksheet"
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ p: 2 }}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ p: 2 }}>
                    Instructions for completing online recommendation forms
                  </Typography>
                </Grid>
                <Grid item container xs={6} spacing={1} sx={{ p: 2 }}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://www.peointernational.org/wp-content/uploads/2025/05/Chapter-Form-Instructions-all-projects-08.18.25.pdf"
                      label="Online Form Instructions"
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ p: 2 }}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ p: 2 }}>
                    Read the full Instruction for Scholarship Committees
                  </Typography>
                </Grid>
                <Grid item container xs={6} spacing={1} sx={{ p: 2 }}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://www.peointernational.org/wp-content/uploads/2023/11/instructionsforscholarshipcommitteesaugust2023.pdf"
                      label="Scholarship Committee Instructions"
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ p: 2 }}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ p: 2 }}>
                    Reporting a Fund Chair Change
                  </Typography>
                </Grid>
                <Grid item container xs={6} spacing={1} sx={{ p: 2 }}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peointernational.formstack.com/forms/fund_chairman_change_form"
                      label="Online Form"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Grid container xs={12} sx={{ p: 2 }}>
                <Grid item xs={12} textAlign="center" sx={{ p: 2 }}>
                  <Link
                    href={ichFormLink()}
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #509E2F 0%, #0072CE 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Individual Case History Form
                    </Button>
                  </Link>
                </Grid>
                <Grid item xs={2} />
                <Grid item xs={8} textAlign="center">
                  <Typography variant="caption">
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                  </Typography>
                </Grid>
              </Grid>
            </CardActions>
          </Card>

          {/* Local Chapter Timeline for Awarding a Scholarship */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title="Local Chapter Timeline for Awarding a Scholarship"
              sx={{
                background: 'linear-gradient(135deg, #509E2F 0%, #0072CE 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container xs={12}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    The scholarship committee decides on a recipient.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    The scholarship committee chair completes an Individual Case
                    History (ICH) for each recipient. Do NOT notify the
                    recipient of the scholarship award yet. The ICH must be
                    approved first.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    The P.E.O. Foundation trustees review and approve the ICH.
                    Allow up to four weeks for review.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    Upon approval, an email is sent to the scholarship committee
                    chair. If the committee wishes to notify the recipient, it
                    must be done within seven calendar days. After that, an
                    automatically generated email is sent to the recipient
                    notifying her of the award.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    The recipient will be emailed a link to set up her personal
                    portal. This portal allows the recipient to promptly
                    complete the online Acceptance of Scholarship form. After
                    the form is submitted, it is reviewed by the P.E.O.
                    Foundation Scholarship Supervisor. After approval, the
                    recipient can secure the Confirmation of Enrollment from the
                    school (after July 1), upload it and submit it through the
                    portal.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    The school must confirm the recipient's enrollment for the
                    same school term for which the scholarship is being given,
                    e.g. if the scholarship is for the upcoming fall semester,
                    then the school must confirm enrollment for that same
                    semester. The P.E.O. Foundation office confirms enrollment
                    directly with Cottey College.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 2, lineHeight: 1.6 }}>
                    <Circle sx={{ fontSize: 10, pr: 2, color: '#509E2F' }} />
                    Please note: scholarships CANNOT be paid before July 1.
                    Scholarships are paid between July 1 and March 1. Funds are
                    paid via direct deposit or check to the student or by check
                    to the school.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* File Management Tab */}
        {canUploadForm() && (
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              {/* Upload Section */}
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
                  <CardHeader
                    title="Upload Foundation Files"
                    avatar={<CloudUpload color="primary" />}
                    sx={{
                      background:
                        'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Upload Foundation Individual Case History files and
                      supporting documents for processing.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Upload />}
                      onClick={() => setUploadDialogOpen(true)}
                      fullWidth
                      sx={{ py: 1.5 }}
                    >
                      Upload New File
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Statistics Section */}
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
                  <CardHeader
                    title="Upload Statistics"
                    avatar={<History color="primary" />}
                    sx={{
                      background:
                        'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="#1976d2"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {testFileHistory.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Files
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="success.main"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {
                            testFileHistory.filter(
                              (f) => f.status === 'Completed'
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Completed
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="warning.main"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {
                            testFileHistory.filter(
                              (f) => f.status === 'Processing'
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Processing
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="h4"
                          color="error.main"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {
                            testFileHistory.filter(
                              (f) => f.number_of_errors > 0
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          With Errors
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* File History Table */}
              <Grid item xs={12}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                  <CardHeader
                    title="File History"
                    avatar={<History color="primary" />}
                    sx={{
                      background:
                        'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  />
                  <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              File Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Uploaded By
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Upload Date
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Errors
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {testFileHistory.map((file) => (
                            <React.Fragment key={file.id}>
                              <TableRow hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {file.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>{file.uploaded_by}</TableCell>
                                <TableCell>
                                  {formatDate(file.uploaded_at)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={file.status}
                                    color={getStatusColor(file.status) as any}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {file.number_of_errors > 0 ? (
                                    <Chip
                                      label={`${file.number_of_errors} errors`}
                                      color="error"
                                      size="small"
                                    />
                                  ) : (
                                    <Chip
                                      label="No errors"
                                      color="success"
                                      size="small"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      title="View Errors"
                                      onClick={() =>
                                        toggleRowExpansion(file.id)
                                      }
                                      disabled={file.number_of_errors === 0}
                                    >
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      title="Download"
                                    >
                                      <Download fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      title="Delete"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {/* Error Details Row */}
                              {file.number_of_errors > 0 && (
                                <TableRow>
                                  <TableCell colSpan={6} sx={{ p: 0 }}>
                                    <Collapse
                                      in={expandedRows[file.id]}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <Box
                                        sx={{
                                          p: 2,
                                          backgroundColor: '#fff3e0',
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          color="warning.main"
                                          sx={{ mb: 1, fontWeight: 'bold' }}
                                        >
                                          Errors Found:
                                        </Typography>
                                        {mockErrors[file.id]?.map(
                                          (error, index) => (
                                            <Typography
                                              key={index}
                                              variant="body2"
                                              sx={{ mb: 0.5, pl: 2 }}
                                            >
                                              • {error}
                                            </Typography>
                                          )
                                        )}
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        )}
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Foundation File</DialogTitle>
        <DialogContent>
          {uploadSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              File uploaded successfully!
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a file to upload for Foundation processing. Supported
                formats: XLS, XLSX, CSV
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: 'block' }}
              >
                Note: CSV files should use comma separation and UTF-8 encoding
                for best compatibility.
              </Typography>
              <input
                accept=".xls,.xlsx,.csv"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  fullWidth
                  sx={{ py: 2, borderStyle: 'dashed' }}
                >
                  {selectedFile ? selectedFile.name : 'Choose File'}
                </Button>
              </label>
              {selectedFile && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: 'primary.main' }}
                >
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            {uploadSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!uploadSuccess && (
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              variant="contained"
              startIcon={uploading ? <LinearProgress /> : <Upload />}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FoundationLandingPage;
